import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ActivityBar from './ActivityBar';
import SidebarFooter from './SidebarFooter';
import RenderTracker from '../studio/RenderTracker';
import { useAuth, useIsAdmin } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useWorkstation } from '../../contexts/WorkstationContext';
import { useIsDesktop } from '../../hooks/useMediaQuery';

/**
 * THE SIGNED-IN SHELL — the activity bar, plus whatever page is open beside it.
 *
 * Owner 2026-08-23: *"when I click the wallet icon on far left, the navigation bar is gone — it needs to
 * always be there for navigation convenience."* It was mounted inside `ChatWorkspace`, so every route that
 * was not the workspace — the wallet, settings, admin — dropped it and left the creator with a browser back
 * button as their only way home. Navigation chrome belongs at the route boundary, so it now wraps EVERY
 * protected page and the bar is genuinely always there.
 *
 * Desktop only: a phone is a viewer (RF3-DEVICE), and those pages keep their own full-width layout.
 *
 * ONE TREE, BOTH WIDTHS (BUGS.md 2026-09-12). This used to return `<>{children}</>` on a narrow screen and a
 * wrapped tree on a wide one. React reconciles by POSITION, so children sitting at a different depth either
 * side of the breakpoint is not a re-render — it is an unmount and a remount, and every piece of component
 * state inside goes with it. Crossing the breakpoint while a project was open therefore blanked it: the URL
 * was unchanged and the server still had the data, but the page came back as an empty untitled show, which
 * invites a creator to type over a document that already has content.
 *
 * So the shape is now CONSTANT and only the styling changes. `display: contents` makes both wrappers
 * layout-transparent on a phone, so those pages lay out exactly as they did when the wrappers were absent —
 * same rendering, without paying for it with the studio's state.
 */
const WorkstationChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut, updateAlias } = useAuth();
  const isAdmin = useIsAdmin();
  const { effectiveTheme, setThemeMode } = useTheme();
  const { left, right } = useWorkstation();
  const isDesktop = useIsDesktop();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  /** Identity, in the one place it lives — the same component the explorer used to carry at its foot. */
  const account = (
    <SidebarFooter
      name={user?.name || ''}
      email={user?.email || ''}
      avatar={user?.avatar}
      alias={user?.alias}
      theme={effectiveTheme === 'dark' ? 'dark' : 'light'}
      onToggleTheme={() => setThemeMode(effectiveTheme === 'dark' ? 'light' : 'dark')}
      onSignOut={() => signOut()}
      onSaveAlias={(a) => updateAlias(a)}
    />
  );

  /** The productions view is the active one on the workstation routes; anywhere else the icon navigates. */
  const onStudio = pathname.startsWith('/dashboard') || pathname.startsWith('/group');

  /**
   * "SHOW ME MY WORKSTATION" — the one button that must always end with the work visible (owner 2026-08-24:
   * *"can't see left pane and right pane on Your productions icon"*).
   *
   * The fold is remembered across reloads and across routes, so arriving from the wallet with both panes
   * folded meant landing on a bare canvas — and a plain toggle only offered to fold the remaining one. So:
   * anything folded -> unfold BOTH; everything already open on the workstation -> fold the explorer, which is
   * the VS Code gesture and the only case where hiding is what was asked for.
   */
  /** Where the creator is standing, for the bar's active mark. */
  const activeView = pathname.startsWith('/store') ? 'store' as const
    : pathname.startsWith('/wallet') ? 'wallet' as const
    : pathname.startsWith('/admin') ? 'admin' as const
      : pathname.startsWith('/account-settings') ? 'settings' as const
        : onStudio ? 'productions' as const : undefined;

  const showProductions = () => {
    if (!onStudio) navigate('/dashboard');
    if (left.collapsed || right.collapsed) {
      if (left.collapsed) left.toggle();
      if (right.collapsed) right.toggle();
      return;
    }
    if (onStudio) left.toggle();
  };

  return (
    <div className={isDesktop ? 'flex h-[100dvh] w-screen overflow-hidden bg-white dark:bg-black' : 'contents'}>
      {isDesktop && (
        <ActivityBar
          explorerOpen={!left.collapsed}
          onProductions={showProductions}
          activeView={activeView}
          onStudio={onStudio}
          isAdmin={isAdmin}
          avatar={user?.avatar}
          name={user?.alias || user?.name || user?.email || ''}
          renderSignal={<RenderTracker dot />}
          account={account}
        />
      )}
      {/* The page owns its own scrolling; the shell only reserves the column beside the bar. On a phone this
          wrapper is layout-transparent, so the page keeps the full width it always had. */}
      <div className={isDesktop ? 'min-w-0 flex-1 overflow-y-auto' : 'contents'}>{children}</div>
    </div>
  );
};

export default WorkstationChrome;
